/**
 * GovernanceMarketplacePage.jsx
 *
 * Dashboard de gestão do Azure Marketplace — acessível em /governance/marketplace
 * Exibe todas as subscriptions ativas, status de webhooks e operações pendentes.
 *
 * NOTA: A landing page de ativação (recebida da Microsoft) é STANDALONE:
 *   /marketplace/activate?token=<opaque>  →  MarketplaceActivationPage.jsx
 *   Esta página é apenas para administração interna (Founder/SuperAdmin).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AZURE } from '../../services/api';
import R from '../../navigation/opsRoutes';
import PageHeader from '../../components/layout/PageHeader';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

// ── Status badges ─────────────────────────────────────────────────────────────
const getStatusMap = (lang) => ({
  Subscribed:              { color: 'bg-green-100 text-green-700',  dot: 'bg-green-500',  label: lang === 'en' ? 'Active'    : 'Ativo' },
  Suspended:               { color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500', label: lang === 'en' ? 'Suspended' : 'Suspenso' },
  Unsubscribed:            { color: 'bg-red-100 text-red-700',      dot: 'bg-red-500',    label: lang === 'en' ? 'Cancelled' : 'Cancelado' },
  PendingFulfillmentStart: { color: 'bg-blue-100 text-blue-700',    dot: 'bg-blue-500',   label: lang === 'en' ? 'Pending'   : 'Pendente' },
});

function StatusBadge({ status }) {
  const { lang } = useGovernanceLang();
  const STATUS_MAP = getStatusMap(lang);
  const s = STATUS_MAP[status] || { color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400', label: status || '—' };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full ${s.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD  = '#b48c2c';

// ── Dados dos Produtos ────────────────────────────────────────────────────────
const getProducts = (lang) => {
  const e = lang === 'en';
  return [
  {
    id: 'governance',
    offer: 'vukz-governance-os',
    name: 'VUKZ Governance AI',
    tagline: e
      ? 'Governance operating system with embedded AI agents for companies, clubs and startups.'
      : 'Sistema operacional de governança com agentes de IA para empresas, clubes e startups.',
    category: 'SaaS · Business Operations · AI App',
    pitch: e
      ? 'Turn planning, financials, projects, commercial operations and documentation into a living system powered by embedded AI agents. Customers buy the complete solution — not infrastructure.'
      : 'Transformar planejamento, financeiro, projetos, comercial e documentação em um sistema vivo com agentes de IA embutidos. O cliente não compra infra — compra a solução completa.',
    color: '#064e3b',
    lightColor: '#f0fdf4',
    icon: '🏛️',
    includes: e ? [
      'Governance web portal (front included)',
      'Multi-tenant backend',
      'Per-module AI agents',
      'Per-customer database (tenant)',
      'Projects / Planner module',
      'Financials and projections',
      'Commercial / Plans',
      'Investor materials',
      'HR and headcount',
      'Marketing and SEO',
      'Documents and reports',
      'Role-based access control',
      'Assisted onboarding',
    ] : [
      'Portal web de governança (front incluído)',
      'Backend multi-tenant',
      'Agentes de IA por módulo',
      'Banco de dados por cliente (tenant)',
      'Módulo de Projetos / Planner',
      'Financeiro e projeções',
      'Comercial / Planos',
      'Material para Investidores',
      'RH e headcount',
      'Marketing e SEO',
      'Documentos e relatórios',
      'Controle de acesso por perfil',
      'Onboarding assistido',
    ],
    notNow: e ? [
      'Agent infrastructure (invisible)',
      'Source code / installed license',
      'Azure deployment on customer side (future enterprise)',
    ] : [
      'Infraestrutura dos agentes (invisível)',
      'Código-fonte / licença instalada',
      'Deploy no Azure do cliente (futuro enterprise)',
    ],
    plans: [
      {
        code: 'governance-starter',
        name: 'Governance Starter',
        price: e ? 'US$ 299 – 499/mo' : 'US$ 299 – 499/mês',
        target: e ? 'Small companies, projects and startups' : 'Pequenas empresas, projetos e startups',
        limits: e
          ? '1 organization · up to 3 users · Overview, Projects, Basic Financials, Documents, General Agent · Monthly AI usage limit'
          : '1 organização · até 3 usuários · Overview, Projetos, Financeiro básico, Documentos, Agente geral · Limite mensal de uso de IA',
        badge: null,
      },
      {
        code: 'governance-professional',
        name: 'Governance Pro',
        price: e ? 'US$ 899 – 1,499/mo' : 'US$ 899 – 1.499/mês',
        target: e ? 'Companies, clubs, academies and operating startups' : 'Empresas, clubes, academias e startups em operação',
        limits: e
          ? 'Up to 10 users · Projects, Financials, Commercial, Marketing, Investors, HR · Per-module agents · PDF export · Dashboards · Access control · Standard support'
          : 'Até 10 usuários · Projetos, Financeiro, Comercial, Marketing, Investidores, RH · Agentes por módulo · Exportação PDF · Dashboards · Controle de acesso · Suporte padrão',
        badge: e ? 'RECOMMENDED' : 'RECOMENDADO',
      },
      {
        code: 'governance-enterprise',
        name: 'Governance Enterprise',
        price: e ? 'US$ 2,500+/mo + setup' : 'US$ 2.500+/mês + setup',
        target: e ? 'Large clubs, federations, networks, business groups' : 'Clubes grandes, federações, redes, grupos empresariais',
        limits: e
          ? 'Negotiated users · Multiple modules · Customization · Internal data integration · Priority support · Private offer'
          : 'Usuários negociados · Múltiplos módulos · Customização · Integração com dados internos · Suporte prioritário · Private offer',
        badge: 'PRIVATE OFFER',
      },
    ],
  },
  {
    id: 'sports',
    offer: 'vukz-sports-intelligence',
    name: 'VUKZ Sports Intelligence',
    tagline: e
      ? 'Sports intelligence platform with video analysis, performance data and AI agents. Built for swimming and athletics, expanding to multi-sport.'
      : 'Plataforma de inteligência esportiva com análise de vídeo, dados de performance e agentes de IA. Desenvolvida para natação e atletismo, expansível para múltiplos esportes.',
    category: 'SaaS · Sports Analytics · Computer Vision · AI App',
    pitch: e
      ? 'Customers buy performance outcomes and technical analysis. SwimVision, TrackVision, VUKZ Assist and VUKZ Data deliver automated reports based on video and real data — not infrastructure.'
      : 'O cliente compra resultado de performance e análise técnica. SwimVision, TrackVision, VUKZ Assist e VUKZ Data entregam relatórios automáticos com base em vídeo e dados reais — não infraestrutura.',
    color: '#1d4ed8',
    lightColor: '#eff6ff',
    icon: '🏊',
    includes: e ? [
      'Sports web portal (front included)',
      'Backend + video processing',
      'SwimVision — swimming technical analysis',
      'TrackVision — athletics technical analysis',
      'VUKZ Assist — conversational agent per role: athlete, coach, strength & conditioning coach, manager, sports nutritionist and biomechanics specialist',
      'VUKZ Data — results database & benchmarks',
      'Automatic technical reports',
      'Per-role dashboard (athlete, coach, prep, manager, nutritionist, biomechanics)',
      'Historical data',
      'Complete plan (all modules) or individual modules',
      'Limits per video volume, users and processing',
    ] : [
      'Portal web esportivo (front incluído)',
      'Backend + processamento de vídeo',
      'SwimVision — análise técnica de natação',
      'TrackVision — análise técnica de atletismo',
      'VUKZ Assist — agente conversacional por perfil: atleta, treinador, preparador físico, gestor, nutricionista e biomecânico',
      'VUKZ Data — banco de resultados e benchmarks',
      'Relatórios técnicos automáticos',
      'Dashboard por perfil (atleta, treinador, prep, gestor, nutricionista, biomecânico)',
      'Dados históricos',
      'Plano completo (todos os módulos) ou por módulos individuais',
      'Limites por volume de vídeos, usuários e processamento',
    ],
    notNow: e ? [
      'Mobile app (future add-on)',
      'Reakt sensors (future hardware/add-on)',
      'VUKZ Recovery — medical/physiotherapy module (future)',
    ] : [
      'App mobile (futuro add-on)',
      'Sensores Reakt (futuro hardware/add-on)',
      'VUKZ Recovery — módulo médico/fisioterapia (futuro)',
    ],
    plans: [
      {
        code: 'sports-modulo',
        name: e ? 'Sports Module' : 'Sports Módulo',
        price: e ? 'US$ 99 – 299/mo per module' : 'US$ 99 – 299/mês por módulo',
        target: e ? 'Individual professional or club starting with 1 role' : 'Profissional avulso ou clube que quer começar por 1 perfil',
        limits: e
          ? 'Mandatory: Coach + Athlete · choose up to 1 additional module: Strength & Conditioning · Manager · Sports Nutritionist · Biomechanics Specialist · Basic VUKZ Assist per active role · Up to 10 users · SwimVision or TrackVision optional'
          : 'Inclui obrigatoriamente Treinador + Atleta · escolha até 1 módulo adicional: Prep. Físico · Gestor · Nutricionista · Biomecânico · VUKZ Assist básico por perfil ativo · Até 10 usuários · SwimVision ou TrackVision opcional',
        badge: null,
      },
      {
        code: 'sports-team',
        name: 'Sports Team',
        price: e ? 'US$ 499 – 999/mo' : 'US$ 499 – 999/mês',
        target: e ? 'Small club / team — up to 3 modules' : 'Clube pequeno / equipe — até 3 módulos',
        limits: e
          ? 'Up to 3 combined modules · up to 50 total users · SwimVision or TrackVision · Technical reports · VUKZ Assist per active role · Monthly video limit · Overage: US$ 99/100 videos'
          : 'Até 3 módulos combinados · até 50 usuários total · SwimVision ou TrackVision · Relatórios técnicos · VUKZ Assist por perfil ativo · Limite mensal de vídeos · Excedente: US$ 99/100 vídeos',
        badge: e ? 'RECOMMENDED' : 'RECOMENDADO',
      },
      {
        code: 'sports-completo',
        name: e ? 'Sports Complete Suite' : 'Sports Completo',
        price: e ? 'US$ 1,500 – 3,000/mo' : 'US$ 1.500 – 3.000/mês',
        target: e ? 'Structured club — all roles' : 'Clube estruturado — todos os perfis',
        limits: e
          ? 'All modules: Athlete + Coach + Strength & Conditioning + Manager + Sports Nutritionist + Biomechanics Specialist · SwimVision + TrackVision · VUKZ Assist for each role · Up to 200 users · Management dashboards · Historical data · Assisted support'
          : 'Todos os módulos: Atleta + Treinador + Prep + Gestor + Nutricionista + Biomecânico · SwimVision + TrackVision · VUKZ Assist para cada perfil · Até 200 usuários · Dashboards de gestão · Dados históricos · Suporte assistido',
        badge: null,
      },
      {
        code: 'sports-enterprise',
        name: 'Sports Enterprise / Federation',
        price: e ? 'US$ 5,000+/mo + setup' : 'US$ 5.000+/mês + setup',
        target: e ? 'Federations, confederations, large clubs, national projects' : 'Federações, confederações, clubes grandes, projetos nacionais',
        limits: e
          ? 'All modules · negotiated athlete/video volume · customization · integrations · BI · institutional reports · Private offer'
          : 'Todos os módulos · volume negociado de atletas/vídeos · customização · integrações · BI · relatórios institucionais · Private offer',
        badge: 'PRIVATE OFFER',
      },
    ],
  },
  ];
};

const getModeloCobranca = (lang) => lang === 'en' ? [
  { item: 'Base subscription',    desc: 'Monthly or annual — includes user, video, report and AI usage limits' },
  { item: 'User overage',         desc: 'Governance: per user/month · Sports: per athlete/month' },
  { item: 'Video overage',        desc: 'Sports: US$ 99 per pack of 100 videos beyond the plan' },
  { item: 'Additional module',    desc: 'Sports: add extra role (e.g. Sports Nutritionist on top of Team plan with 3 modules) — price per module/month' },
  { item: 'Additional storage',   desc: 'Azure Storage cost pass-through with margin' },
  { item: 'Enterprise setup',     desc: 'One-time assisted onboarding fee — negotiated via private offer' },
  { item: 'Private offer',        desc: 'Custom price, term and conditions for strategic customers' },
] : [
  { item: 'Assinatura base',       desc: 'Mensal ou anual — inclui limites de usuários, vídeos, relatórios e uso de IA' },
  { item: 'Excedente de usuários', desc: 'Governance: por usuário/mês · Sports: por atleta/mês' },
  { item: 'Excedente de vídeos',   desc: 'Sports: US$ 99 por pacote de 100 vídeos além do plano' },
  { item: 'Módulo adicional',      desc: 'Sports: adicionar perfil extra (ex: Nutricionista sobre plano Team com 3 módulos) — preço por módulo/mês' },
  { item: 'Armazenamento adicional', desc: 'Repasse com margem do custo Azure Storage' },
  { item: 'Setup Enterprise',      desc: 'Taxa única de implantação assistida — negociada via private offer' },
  { item: 'Private offer',         desc: 'Preço, prazo e condições customizadas para clientes estratégicos no Brasil' },
];

const getOfertaOrdem = (lang) => lang === 'en' ? [
  { order: '1st', name: 'VUKZ Governance AI',              reason: 'More generic, more B2B, easier for Microsoft to understand, less dependent on perfect model performance. Closest to a replicable product.' },
  { order: '2nd', name: 'VUKZ Sports Intelligence',        reason: 'More differentiated product, but requires demos, reports, stable pipeline and clearer use cases. SwimVision and TrackVision maturing.' },
  { order: '3rd+', name: 'Specific offers / private plans', reason: 'SwimVision for Clubs, TrackVision for Athletics, VUKZ Federation Intelligence, VUKZ Academy Performance, VUKZ Investor Governance Pack.' },
] : [
  { order: '1º', name: 'VUKZ Governance AI',              reason: 'Mais genérico, mais B2B, mais fácil para Microsoft entender, menos dependente de performance perfeita dos modelos. Mais próximo de produto replicável.' },
  { order: '2º', name: 'VUKZ Sports Intelligence',        reason: 'Produto mais diferenciado, mas precisa de demos, relatórios, pipeline estável e casos de uso mais claros. SwimVision e TrackVision em maturação.' },
  { order: '3º+', name: 'Ofertas específicas / private plans', reason: 'SwimVision for Clubs, TrackVision for Athletics, VUKZ Federation Intelligence, VUKZ Academy Performance, VUKZ Investor Governance Pack.' },
];

const getCamadas = (lang) => lang === 'en' ? [
  { camada: 'Web front-end',             incluso: true,  como: 'VUKZ portal included in the plan' },
  { camada: 'Backend / API',             incluso: true,  como: 'Invisible — part of the SaaS' },
  { camada: 'AI Agents',                 incluso: true,  como: 'Core product differentiator' },
  { camada: 'Database',                  incluso: true,  como: 'Isolated tenant per customer' },
  { camada: 'Azure Infrastructure',      incluso: true,  como: 'Managed by VUKZ — not sold separately' },
  { camada: 'Customization',             incluso: false, como: 'Enterprise / private offer only' },
  { camada: 'Mobile app',                incluso: false, como: 'Future add-on or higher plan' },
  { camada: 'Reakt sensors',             incluso: false, como: 'Future hardware/add-on' },
  { camada: 'Customer-side Azure deploy', incluso: false, como: 'Future — Managed App enterprise' },
] : [
  { camada: 'Front web',           incluso: true,  como: 'Portal VUKZ incluído no plano' },
  { camada: 'Backend / API',       incluso: true,  como: 'Invisível — parte do SaaS' },
  { camada: 'Agentes de IA',       incluso: true,  como: 'Diferencial central do produto' },
  { camada: 'Banco de dados',      incluso: true,  como: 'Tenant isolado por cliente' },
  { camada: 'Infra Azure',         incluso: true,  como: 'Gerida por você — não vendida separadamente' },
  { camada: 'Customização',        incluso: false, como: 'Apenas Enterprise / private offer' },
  { camada: 'App mobile',          incluso: false, como: 'Futuro add-on ou plano maior' },
  { camada: 'Sensores Reakt',      incluso: false, como: 'Futuro hardware/add-on' },
  { camada: 'Deploy no Azure do cliente', incluso: false, como: 'Futuro — Managed App enterprise' },
];

// ── Tab: Produtos ─────────────────────────────────────────────────────────────
function TabProdutos() {
  const { lang } = useGovernanceLang();
  const PRODUCTS = getProducts(lang);
  const MODELO_COBRANCA = getModeloCobranca(lang);
  const OFERTA_ORDEM = getOfertaOrdem(lang);
  const CAMADAS = getCamadas(lang);
  const e = lang === 'en';
  const [activeProduct, setActiveProduct] = useState('governance');
  const product = PRODUCTS.find(p => p.id === activeProduct);

  return (
    <div className="space-y-6">

      {/* Seletor de produto */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {PRODUCTS.map(p => (
          <button
            key={p.id}
            onClick={() => setActiveProduct(p.id)}
            className={`text-left rounded-xl border-2 p-5 transition-all ${
              activeProduct === p.id ? 'shadow-md' : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
            style={activeProduct === p.id ? { borderColor: p.color, background: p.lightColor } : {}}
          >
            <div className="text-3xl mb-2">{p.icon}</div>
            <div className="font-extrabold text-lg" style={{ color: activeProduct === p.id ? p.color : '#111827' }}>{p.name}</div>
            <div className="text-xs text-gray-500 mt-1">{p.category}</div>
          </button>
        ))}
      </div>

      {/* Pitch */}
      <div className="rounded-xl border px-5 py-4" style={{ background: product.lightColor, borderColor: `${product.color}30` }}>
        <div className="text-sm font-semibold mb-1" style={{ color: product.color }}>{e ? '💡 Value proposition' : '💡 Proposta de valor'}</div>
        <p className="text-gray-700 text-sm">{product.pitch}</p>
      </div>

      {/* O que entra + O que não vender agora */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <div className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <span className="text-green-600">✓</span> {e ? "What's included" : 'O que entra no produto'}
          </div>
          <ul className="space-y-1.5">
            {product.includes.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-green-500 mt-0.5 shrink-0">•</span>{item}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <div className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <span className="text-gray-400">○</span> {e ? 'Not included yet' : 'Não vender agora'}
          </div>
          <ul className="space-y-1.5">
            {product.notNow.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-500">
                <span className="text-gray-300 mt-0.5 shrink-0">•</span>{item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Planos */}
      <div>
        <div className="font-bold text-gray-900 mb-3">{e ? 'Marketplace Plans' : 'Planos no Marketplace'}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {product.plans.map(plan => (
            <div
              key={plan.code}
              className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-3 relative"
            >
              {plan.badge && (
                <span
                  className="absolute top-3 right-3 text-[9px] font-black px-1.5 py-0.5 rounded"
                  style={plan.badge === 'RECOMENDADO'
                    ? { background: BRAND_GOLD, color: '#fff' }
                    : { background: '#6b7280', color: '#fff' }}
                >
                  {plan.badge}
                </span>
              )}
              <div>
                <div className="font-extrabold text-gray-900 text-base">{plan.name}</div>
                <div className="text-xs text-gray-500 mt-0.5">{plan.target}</div>
              </div>
              <div className="text-xl font-black" style={{ color: product.color }}>{plan.price}</div>
              <div className="text-xs text-gray-500 leading-relaxed border-t pt-3">{plan.limits}</div>
              <div className="mt-auto">
                <code className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{plan.code}</code>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modelo de cobrança */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-5 py-4 border-b font-bold text-gray-900">{e ? 'Billing model: base subscription + overages' : 'Modelo de cobrança: assinatura base + excedentes'}</div>
        <table className="w-full text-sm">
          <tbody className="divide-y">
            {MODELO_COBRANCA.map((m, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-5 py-3 font-semibold text-gray-800 w-48 shrink-0">{m.item}</td>
                <td className="px-5 py-3 text-gray-500">{m.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Camadas do produto */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-5 py-4 border-b font-bold text-gray-900">{e ? 'What the customer gets — product layers' : 'O que o cliente recebe — camadas do produto'}</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="px-5 py-3 text-left font-semibold text-gray-600">{e ? 'Layer' : 'Camada'}</th>
              <th className="px-5 py-3 text-left font-semibold text-gray-600">{e ? 'Included?' : 'No produto?'}</th>
              <th className="px-5 py-3 text-left font-semibold text-gray-600">{e ? 'How to sell' : 'Como vender'}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {CAMADAS.map((c, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-5 py-3 font-medium text-gray-800">{c.camada}</td>
                <td className="px-5 py-3">
                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${c.incluso ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {c.incluso ? (e ? '✓ Included' : '✓ Incluído') : (e ? '– Not yet' : '– Não agora')}
                  </span>
                </td>
                <td className="px-5 py-3 text-xs text-gray-500">{c.como}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ordem de cadastro */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-5 py-4 border-b font-bold text-gray-900">{e ? 'Recommended registration order on Marketplace' : 'Ordem recomendada de cadastro no Marketplace'}</div>
        <div className="divide-y">
          {OFERTA_ORDEM.map((o, i) => (
            <div key={i} className="px-5 py-4 flex items-start gap-4">
              <span
                className="text-lg font-black w-10 shrink-0 text-center rounded-lg py-1"
                style={{ background: i === 0 ? BRAND_GOLD : '#f3f4f6', color: i === 0 ? '#fff' : '#374151' }}
              >
                {o.order}
              </span>
              <div>
                <div className="font-bold text-gray-900">{o.name}</div>
                <div className="text-sm text-gray-500 mt-0.5">{o.reason}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Frase-síntese */}
      <div
        className="rounded-xl border px-6 py-5 text-center"
        style={{ background: '#f0fdf4', borderColor: `${BRAND_GREEN}30` }}
      >
        <div className="text-sm text-gray-500 mb-1">{e ? 'Product summary' : 'Frase que resume o produto'}</div>
        <p className="text-base font-bold" style={{ color: BRAND_GREEN }}>
          {e
            ? '"VUKZ sells vertical SaaS platforms with AI agents applied to governance and sports intelligence. The infrastructure is the engine, but what is sold is the complete solution."'
            : '"A VUKZ vende plataformas SaaS verticais com agentes de IA aplicados à governança e à inteligência esportiva. A infraestrutura é o motor, mas o produto vendido é a solução completa."'
          }
        </p>
      </div>
    </div>
  );
}

const OFFER_LABEL = {
  'vukz-governance-os':       'Governance OS',
  'vukz-sports-intelligence':  'Sports Intelligence',
  'vukz-sports-governance':    'Sports Operating System',
};

const PLAN_LABEL = {
  // Governance OS (legacy)
  'governance-starter':        'Starter',
  'governance-professional':   'Professional',
  'governance-enterprise':     'Enterprise',
  // Sports Intelligence
  'sports-starter':            'Sports Starter',
  'sports-club':               'Club Intelligence',
  'sports-federation':         'Federation Intelligence',
  // Sports OS (bundle)
  'sg-starter':                'Starter',
  'sg-professional':           'Professional',
  'sg-enterprise':             'Enterprise',
  // Legacy v1
  'sports-intelligence-base':  'Base',
  'sports-intelligence-pro':   'Pro',
  'starter-sports':            'Sports Starter',
  'club-performance':          'Club Intelligence',
  'federation-enterprise':     'Federation Intelligence',
};

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-1">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="text-3xl font-black" style={{ color: accent || BRAND_GREEN }}>{value ?? '—'}</div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function GovernanceMarketplacePage() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState('all');
  const [search, setSearch]   = useState('');
  const [activeTab, setActiveTab] = useState('subscriptions'); // 'subscriptions' | 'produtos'

  const { lang } = useGovernanceLang();
  const e = lang === 'en';
  const T = {
    subtitle:         e ? 'SaaS subscription management — Sports Intelligence & Sports Operating System offers' : 'Gestão de subscriptions SaaS — Offers Sports Intelligence & Sports Operating System',
    tabSubs:          e ? '📋 Subscriptions' : '📋 Subscriptions',
    tabProducts:      e ? '🧭 Products & Pricing' : '🧭 Produtos & Precificação',
    loading:          e ? 'Loading…' : 'Carregando…',
    refresh:          e ? '↻ Refresh' : '↻ Atualizar',
    statTotal:        e ? 'Total' : 'Total',
    statActive:       e ? 'Active' : 'Ativos',
    statSuspended:    e ? 'Suspended' : 'Suspensos',
    statCancelled:    e ? 'Cancelled' : 'Cancelados',
    searchPlaceholder: e ? 'Search by name, email, offer…' : 'Buscar por nome, e-mail, oferta…',
    fAll:             e ? 'All' : 'Todos',
    fActive:          e ? 'Active' : 'Ativos',
    fSuspended:       e ? 'Suspended' : 'Suspensos',
    fCancelled:       e ? 'Cancelled' : 'Cancelados',
    colSub:           e ? 'Subscription' : 'Assinatura',
    colOffer:         e ? 'Offer / Plan' : 'Oferta / Plano',
    colBuyer:         e ? 'Buyer' : 'Comprador',
    colStatus:        e ? 'Status' : 'Status',
    colActivated:     e ? 'Activated on' : 'Ativado em',
    colWebhook:       e ? 'Last Webhook' : 'Último Webhook',
    emptyNone:        e ? 'No subscriptions found. The first one will arrive via the Marketplace landing page.' : 'Nenhuma subscription encontrada. A primeira virá via landing page do Marketplace.',
    emptyFilter:      e ? 'No subscriptions match the filter.' : 'Nenhuma subscription corresponde ao filtro.',
    licences:         e ? 'licenses' : 'licenças',
    howTitle:         e ? 'How does the activation flow work?' : 'Como funciona o fluxo de ativação?',
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const { data } = await AZURE.get('/marketplace/subscriptions', { params });
      setSubscriptions(data.data || data.items || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao carregar subscriptions.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const filtered = subscriptions.filter(s =>
    !search ||
    (s.subscription_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.buyer_email        || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.offer_id           || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.plan_id            || '').toLowerCase().includes(search.toLowerCase())
  );

  // Stats
  const total       = subscriptions.length;
  const active      = subscriptions.filter(s => s.saas_status === 'Subscribed').length;
  const suspended   = subscriptions.filter(s => s.saas_status === 'Suspended').length;
  const unsubscribed= subscriptions.filter(s => s.saas_status === 'Unsubscribed').length;

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        title="Azure Marketplace"
        subtitle={T.subtitle}
        actions={
          <div className="flex items-center gap-3">
            <a
              href="/marketplace/activate"
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
              target="_blank"
              rel="noreferrer"
            >
              ↗ Landing Page
            </a>
            <button
              onClick={load}
              disabled={loading}
              className="text-xs px-3 py-1.5 rounded-lg text-white font-semibold transition hover:opacity-90 disabled:opacity-50"
              style={{ background: BRAND_GREEN }}
            >
              {loading ? T.loading : T.refresh}
            </button>
          </div>
        }
      />

      <div className="px-6 py-6 max-w-7xl mx-auto space-y-6">

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200">
          {[
            { id: 'subscriptions', label: T.tabSubs },
            { id: 'produtos',      label: T.tabProducts },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-white border border-b-white -mb-px text-gray-900'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              style={activeTab === tab.id ? { borderBottomColor: 'white', color: BRAND_GREEN } : {}}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab: Produtos */}
        {activeTab === 'produtos' && <TabProdutos />}

        {/* Tab: Subscriptions */}
        {activeTab === 'subscriptions' && (<>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard label={T.statTotal}     value={total}        sub="subscriptions" />
          <StatCard label={T.statActive}    value={active}       sub="Subscribed"    accent={BRAND_GREEN} />
          <StatCard label={T.statSuspended} value={suspended}    sub="Suspended"     accent="#d97706" />
          <StatCard label={T.statCancelled} value={unsubscribed} sub="Unsubscribed"  accent="#dc2626" />
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder={T.searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 min-w-[200px] text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-emerald-500"
          />
          {['all', 'Subscribed', 'Suspended', 'Unsubscribed'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition ${
                filter === f
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={filter === f ? { background: BRAND_GREEN } : {}}
            >
              {{ all: T.fAll, Subscribed: T.fActive, Suspended: T.fSuspended, Unsubscribed: T.fCancelled }[f]}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 flex justify-center">
              <svg className="animate-spin w-8 h-8 text-emerald-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-4xl mb-3">📭</div>
              <div className="text-gray-500 text-sm">
                  {subscriptions.length === 0 ? T.emptyNone : T.emptyFilter}
              </div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{T.colSub}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{T.colOffer}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{T.colBuyer}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{T.colStatus}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{T.colActivated}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{T.colWebhook}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((sub, i) => (
                  <tr key={sub.subscription_id || i} className="hover:bg-slate-50 transition">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900 truncate max-w-[200px]">
                        {sub.subscription_name || sub.subscription_id}
                      </div>
                      <div className="text-xs text-gray-400 font-mono truncate max-w-[200px]">
                        {sub.subscription_id}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-700">
                        {OFFER_LABEL[sub.offer_id] || sub.offer_id || '—'}
                      </div>
                      <div className="text-xs text-gray-400">
                        {PLAN_LABEL[sub.plan_id] || sub.plan_id || '—'}
                        {sub.quantity > 1 ? ` · ${sub.quantity} ${T.licences}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-700 truncate max-w-[180px]">
                        {sub.buyer_email || '—'}
                      </div>
                      {sub.institution_id && (
                        <div className="text-xs text-gray-400">inst #{sub.institution_id}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={sub.saas_status} />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {sub.activated_at
                        ? new Date(sub.activated_at).toLocaleDateString(e ? 'en-US' : 'pt-BR')
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {sub.last_webhook_action ? (
                        <div>
                          <div className="text-xs font-medium text-gray-700">{sub.last_webhook_action}</div>
                          <div className="text-xs text-gray-400">
                            {sub.last_webhook_at
                              ? new Date(sub.last_webhook_at).toLocaleDateString(e ? 'en-US' : 'pt-BR')
                              : '—'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Info footer */}
        <div
          className="rounded-xl border px-5 py-4 text-sm"
          style={{ background: '#f0fdf4', borderColor: `${BRAND_GREEN}30` }}
        >
          <div className="font-semibold mb-1" style={{ color: BRAND_GREEN }}>
            {T.howTitle}
          </div>
          <ol className="text-gray-600 space-y-0.5 list-decimal list-inside">
            {e ? (
              <>
                <li>Buyer purchases on Azure Marketplace → receives link with <code className="bg-white px-1 rounded">?token=</code></li>
                <li>Visits <strong>/marketplace/activate</strong> → resolves token → confirms → activates</li>
                <li>Microsoft sends webhooks (ChangePlan, Suspend, Unsubscribe…) → <code className="bg-white px-1 rounded">POST /api/marketplace/webhook</code></li>
                <li>Operations requiring ACK (ChangePlan, ChangeQuantity, Reinstate) are confirmed automatically via Operations API</li>
              </>
            ) : (
              <>
                <li>Comprador adquire no Azure Marketplace → recebe link com <code className="bg-white px-1 rounded">?token=</code></li>
                <li>Acessa <strong>/marketplace/activate</strong> → resolve token → confirma → ativa</li>
                <li>Microsoft envia webhooks (ChangePlan, Suspend, Unsubscribe…) → <code className="bg-white px-1 rounded">POST /api/marketplace/webhook</code></li>
                <li>Operações que exigem ACK (ChangePlan, ChangeQuantity, Reinstate) são confirmadas automaticamente via Operations API</li>
              </>
            )}
          </ol>
        </div>

        </>)} {/* end tab subscriptions */}
      </div>
    </div>
  );
}
